import {
  Pill, ShieldAlert, Users, Bot, IdCard, Activity,
  MessagesSquare, Video, Stethoscope, Navigation, HeartPulse, MapPin,
} from "lucide-react";

export const SITE = {
  name: "Kinect Nova AI",
  domain: "kinect-health.com",
  url: "https://kinect-health.com",
  tagline: "Care for the ones you love",
  supportEmail: "kinectnovaai@kinectnovaai.com",
  description:
    "Kinect Nova AI keeps families connected and protected — medication management, Emergency SOS, AI Family Coordinator, medical passport, teleconsultation and more, all in one beautifully simple app.",
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Features", to: "/features" },
  { label: "About", to: "/about" },
  { label: "Help Center", to: "/help" },
  { label: "Contact", to: "/contact" },
];

export const FEATURES = [
  {
    icon: Pill,
    title: "Medication Management",
    slug: "medication-management",
    short: "Smart reminders, refill alerts and adherence tracking for the whole family.",
    long: "Never miss a dose again. Schedule medications with smart reminders, track adherence over time, get low-supply refill alerts, and let caregivers monitor a loved one's routine — all with privacy at the core.",
    image: "https://images.unsplash.com/photo-1537735319956-df7db4b6a4e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: ShieldAlert,
    title: "Emergency SOS",
    slug: "emergency-sos",
    short: "One tap instantly alerts your circle with your live location.",
    long: "In a crisis, every second counts. A single tap fires an SOS to your Emergency Circle with your real-time GPS location, medical details and a one-touch call back — even from the lock screen.",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: Users,
    title: "Emergency Circle",
    slug: "emergency-circle",
    short: "Build a trusted circle that's notified the moment something happens.",
    long: "Invite family, friends and caregivers into a trusted circle. They receive instant notifications during emergencies, can view shared health context, and coordinate care together in real time.",
    image: "https://images.unsplash.com/photo-1559027615-cd4628902d4a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: Bot,
    title: "AI Family Coordinator",
    slug: "ai-family-coordinator",
    short: "An intelligent assistant that organizes care, appointments and tasks.",
    long: "Your AI Family Coordinator summarizes health updates, schedules appointments, assigns caregiving tasks and surfaces what matters — so the whole family stays aligned without the group-chat chaos.",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: IdCard,
    title: "Medical Passport",
    slug: "medical-passport",
    short: "All vital health records in one secure, shareable place.",
    long: "Store allergies, conditions, medications, blood type, insurance and emergency contacts in an encrypted Medical Passport — instantly shareable with doctors or first responders when it matters most.",
    image: "https://images.unsplash.com/photo-1584516150909-c43483ee7932?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: Activity,
    title: "Health Dashboard",
    slug: "health-dashboard",
    short: "A clear, real-time view of vitals, trends and wellbeing.",
    long: "See heart rate, activity, sleep, medications and more in one elegant dashboard. Spot trends early, celebrate progress, and keep caregivers informed with at-a-glance health insights.",
    image: "https://images.unsplash.com/photo-1551076805-e1869033e561?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: MessagesSquare,
    title: "Family Chat",
    slug: "family-chat",
    short: "Private, secure messaging built for coordinating care.",
    long: "A dedicated, end-to-end private space for your family to chat, share updates, photos and care notes — without mixing personal health conversations into everyday social apps.",
    image: "https://images.unsplash.com/photo-1543269865-cbf427effbad?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: Video,
    title: "Voice & Video Calls",
    slug: "voice-video-calls",
    short: "Crystal-clear calls to check in on the people you love.",
    long: "Make secure one-to-one or group voice and video calls right inside Kinect Nova AI. Perfect for daily check-ins, helping a parent through a task, or joining a doctor's visit remotely.",
    image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: Stethoscope,
    title: "Doctor Teleconsultation",
    slug: "doctor-teleconsultation",
    short: "Connect with licensed doctors from home, on demand.",
    long: "Book and join secure teleconsultations with licensed clinicians. Share your Medical Passport ahead of time so your doctor has the full picture from the first second of the call.",
    image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: Navigation,
    title: "GPS Navigation",
    slug: "gps-navigation",
    short: "Live location and turn-by-turn guidance to reach loved ones fast.",
    long: "During an emergency, your circle gets live GPS location with turn-by-turn navigation to reach you or a loved one quickly — plus optional location sharing for everyday peace of mind.",
    image: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    icon: HeartPulse,
    title: "HealthKit & Health Connect",
    slug: "healthkit-health-connect",
    short: "Seamlessly syncs with Apple Health and Android Health Connect.",
    long: "Kinect Nova AI integrates with Apple HealthKit and Android Health Connect to securely sync steps, heart rate, sleep and more — bringing your existing health data together in one trusted hub.",
    image: "https://images.unsplash.com/photo-1576678927484-cc907957088c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
];

export const STEPS = [
  {
    title: "Download & set up your profile",
    desc: "Install Kinect Nova AI, create your secure account and build your Medical Passport in minutes.",
  },
  {
    title: "Invite your Emergency Circle",
    desc: "Add the family, friends and caregivers you trust. Everyone stays connected and ready to help.",
  },
  {
    title: "Stay protected, every day",
    desc: "Manage medications, track health, chat, call and rest easy knowing help is one tap away.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Kinect Nova AI gave our family peace of mind. When Dad pressed SOS, all of us got the alert instantly with his exact location.",
    name: "Maria G.",
    role: "Daughter & caregiver",
    rating: 5,
  },
  {
    quote:
      "Managing Mom's medications used to be a full-time job. Now the reminders and refill alerts handle it for us.",
    name: "James T.",
    role: "Family caregiver",
    rating: 5,
  },
  {
    quote:
      "The Medical Passport saved precious minutes at the ER — the doctors had everything they needed immediately.",
    name: "Priya S.",
    role: "Patient",
    rating: 5,
  },
  {
    quote:
      "The AI Family Coordinator keeps all four of us siblings on the same page. No more scattered group texts.",
    name: "David L.",
    role: "Son",
    rating: 5,
  },
];

export const STATS = [
  { value: "11+", label: "Powerful features" },
  { value: "256-bit", label: "Encryption" },
  { value: "24/7", label: "Emergency ready" },
  { value: "2", label: "Platforms supported" },
];

export const FAQS = [
  {
    q: "Is Kinect Nova AI free to download?",
    a: "Yes. Kinect Nova AI is free to download on both the App Store and Google Play. Some advanced features may be offered through an optional subscription.",
  },
  {
    q: "How does the Emergency SOS work?",
    a: "A single tap sends an alert with your live GPS location and key medical details to everyone in your Emergency Circle. It works even from the lock screen so help is always one tap away.",
  },
  {
    q: "Is my health data secure and private?",
    a: "Absolutely. Your data is encrypted in transit and at rest, and we never sell personal information. You control exactly what is shared and with whom. See our Privacy Policy for full details.",
  },
  {
    q: "Does it work with Apple Health and Android Health Connect?",
    a: "Yes. Kinect Nova AI integrates with Apple HealthKit and Android Health Connect so your existing health data syncs securely with your permission.",
  },
  {
    q: "Can I use Kinect Nova AI to care for elderly parents?",
    a: "That's exactly what it's built for. Caregivers can manage medications, monitor wellbeing, receive emergency alerts and coordinate care — all from their own phone.",
  },
  {
    q: "Which devices are supported?",
    a: "Kinect Nova AI is available for iOS (iPhone) and Android phones. Tablet layouts are also supported for a comfortable experience on larger screens.",
  },
];

export const APP_STORE_URL = "https://apps.apple.com/app/kinect-health";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.kinecthealth.app";

export const HELP_CATEGORIES = [
  {
    icon: Pill,
    title: "Getting Started",
    items: [
      "Creating your account",
      "Setting up your Medical Passport",
      "Inviting your Emergency Circle",
      "Connecting Apple Health or Health Connect",
    ],
  },
  {
    icon: ShieldAlert,
    title: "Emergency & Safety",
    items: [
      "How Emergency SOS works",
      "Configuring SOS contacts",
      "Sharing live location",
      "Testing your SOS safely",
    ],
  },
  {
    icon: Pill,
    title: "Medications",
    items: [
      "Adding a medication",
      "Setting reminders",
      "Tracking adherence",
      "Refill alerts",
    ],
  },
  {
    icon: Stethoscope,
    title: "Teleconsultation",
    items: [
      "Booking an appointment",
      "Joining a video visit",
      "Sharing records with your doctor",
      "Prescriptions & follow-ups",
    ],
  },
  {
    icon: Users,
    title: "Account & Billing",
    items: [
      "Managing your subscription",
      "Updating payment details",
      "Family members & roles",
      "Deleting your account",
    ],
  },
  {
    icon: MapPin,
    title: "Privacy & Data",
    items: [
      "Controlling what you share",
      "Exporting your data",
      "Requesting data deletion",
      "Understanding permissions",
    ],
  },
];

export const LAST_UPDATED = "June 26, 2025";
